/**
 * useAI Hook
 *
 * React hook for AI-assisted text generation in the offer editor.
 */
import { useState, useCallback, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { aiAPI } from '../utils/api';
import {
  AIResponse,
  AIStreamChunk,
  AIConfig,
  AIUsageStats,
  ImprovementType,
  ExpansionLength,
} from '../types/editor';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

// Helper to extract error message from API response
function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    // Handle structured error responses
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (typeof detail === 'object' && detail?.message) {
      return detail.message;
    }
    // Handle rate limiting errors
    if (error.response?.status === 429) {
      return 'Limite richieste raggiunto. Riprova più tardi.';
    }
    if (error.response?.status === 400) {
      return detail || 'Richiesta non valida';
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Errore sconosciuto';
}

interface UseAIOptions {
  offertaId?: string;
  onSuccess?: (content: string) => void;
  onError?: (error: Error) => void;
}

interface AIState {
  isLoading: boolean;
  isStreaming: boolean;
  streamContent: string;
  error: Error | null;
}

export function useAI(options: UseAIOptions = {}) {
  const { offertaId, onSuccess, onError } = options;
  const [state, setState] = useState<AIState>({
    isLoading: false,
    isStreaming: false,
    streamContent: '',
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Query AI status
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: async () => {
      const response = await aiAPI.getStatus();
      return response.data as {
        ai_available: boolean;
        ai_configured: boolean;
        ai_enabled: boolean;
        features?: {
          suggestions: boolean;
          generation: boolean;
          improvement: boolean;
        };
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query AI config
  const { data: aiConfig } = useQuery({
    queryKey: ['ai-config'],
    queryFn: async () => {
      try {
        const response = await aiAPI.getConfig();
        return response.data as AIConfig;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Query usage stats
  const { data: usageStats, refetch: refetchUsageStats } = useQuery({
    queryKey: ['ai-usage-stats'],
    queryFn: async () => {
      const response = await aiAPI.getUsageStats();
      return response.data as AIUsageStats;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!aiStatus?.ai_enabled,
  });

  // Suggest mutation
  const suggestMutation = useMutation({
    mutationFn: async (params: {
      currentText: string;
      context?: string;
      fieldName?: string;
    }) => {
      const response = await aiAPI.suggest({
        current_text: params.currentText,
        context: params.context,
        field_name: params.fieldName,
        offerta_id: offertaId,
      });
      return response.data as AIResponse;
    },
    onSuccess: (data) => {
      refetchUsageStats();
      onSuccess?.(data.content);
    },
    onError: (error: unknown) => {
      toast.error(`Errore suggerimento AI: ${getErrorMessage(error)}`);
      onError?.(error as Error);
    },
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: async (params: {
      sectionType: string;
      context: Record<string, unknown>;
      customInstructions?: string;
    }) => {
      const response = await aiAPI.generate({
        section_type: params.sectionType,
        context: params.context,
        custom_instructions: params.customInstructions,
        offerta_id: offertaId,
      });
      return response.data as AIResponse;
    },
    onSuccess: (data) => {
      refetchUsageStats();
      onSuccess?.(data.content);
    },
    onError: (error: unknown) => {
      toast.error(`Errore generazione AI: ${getErrorMessage(error)}`);
      onError?.(error as Error);
    },
  });

  // Improve mutation
  const improveMutation = useMutation({
    mutationFn: async (params: {
      text: string;
      improvementType: ImprovementType;
      context?: string;
    }) => {
      const response = await aiAPI.improve({
        text: params.text,
        improvement_type: params.improvementType,
        context: params.context,
        offerta_id: offertaId,
      });
      return response.data as AIResponse;
    },
    onSuccess: (data) => {
      refetchUsageStats();
      onSuccess?.(data.content);
    },
    onError: (error: unknown) => {
      toast.error(`Errore miglioramento AI: ${getErrorMessage(error)}`);
      onError?.(error as Error);
    },
  });

  // Expand mutation
  const expandMutation = useMutation({
    mutationFn: async (params: {
      text: string;
      targetLength: ExpansionLength;
      context?: string;
    }) => {
      const response = await aiAPI.expand({
        text: params.text,
        target_length: params.targetLength,
        context: params.context,
        offerta_id: offertaId,
      });
      return response.data as AIResponse;
    },
    onSuccess: (data) => {
      refetchUsageStats();
      onSuccess?.(data.content);
    },
    onError: (error: unknown) => {
      toast.error(`Errore espansione AI: ${getErrorMessage(error)}`);
      onError?.(error as Error);
    },
  });

  // Streaming generation
  const generateStream = useCallback(async (params: {
    sectionType: string;
    context: Record<string, unknown>;
    customInstructions?: string;
    onChunk?: (chunk: AIStreamChunk) => void;
  }) => {
    setState(prev => ({
      ...prev,
      isStreaming: true,
      streamContent: '',
      error: null,
    }));

    abortControllerRef.current = new AbortController();

    try {
      let fullContent = '';

      for await (const chunk of aiAPI.generateStream({
        section_type: params.sectionType,
        context: params.context,
        custom_instructions: params.customInstructions,
        offerta_id: offertaId,
      })) {
        if (chunk.error) {
          throw new Error(chunk.error);
        }

        fullContent += chunk.content;
        setState(prev => ({
          ...prev,
          streamContent: fullContent,
        }));

        params.onChunk?.(chunk as AIStreamChunk);

        if (chunk.is_final) {
          refetchUsageStats();
          onSuccess?.(fullContent);
        }
      }

      return fullContent;

    } catch (error) {
      const err = error as Error;
      setState(prev => ({
        ...prev,
        error: err,
      }));
      toast.error(`Errore streaming AI: ${err.message}`);
      onError?.(err);
      return '';

    } finally {
      setState(prev => ({
        ...prev,
        isStreaming: false,
      }));
      abortControllerRef.current = null;
    }
  }, [offertaId, onSuccess, onError, refetchUsageStats]);

  // Cancel streaming
  const cancelStream = useCallback(() => {
    abortControllerRef.current?.abort();
    setState(prev => ({
      ...prev,
      isStreaming: false,
    }));
  }, []);

  // Helper functions
  const suggest = useCallback((
    currentText: string,
    context?: string,
    fieldName?: string
  ) => {
    return suggestMutation.mutateAsync({ currentText, context, fieldName });
  }, [suggestMutation]);

  const generate = useCallback((
    sectionType: string,
    context: Record<string, unknown>,
    customInstructions?: string
  ) => {
    return generateMutation.mutateAsync({ sectionType, context, customInstructions });
  }, [generateMutation]);

  const improve = useCallback((
    text: string,
    improvementType: ImprovementType = 'general',
    context?: string
  ) => {
    return improveMutation.mutateAsync({ text, improvementType, context });
  }, [improveMutation]);

  const expand = useCallback((
    text: string,
    targetLength: ExpansionLength = 'medium',
    context?: string
  ) => {
    return expandMutation.mutateAsync({ text, targetLength, context });
  }, [expandMutation]);

  return {
    // State
    isLoading: suggestMutation.isPending ||
               generateMutation.isPending ||
               improveMutation.isPending ||
               expandMutation.isPending ||
               state.isStreaming,
    isStreaming: state.isStreaming,
    streamContent: state.streamContent,
    error: state.error,

    // Status and config
    aiStatus,
    aiConfig,
    usageStats,
    isAIEnabled: aiStatus?.ai_enabled ?? false,
    isAIConfigured: aiStatus?.ai_configured ?? false,
    features: aiStatus?.features,

    // Actions
    suggest,
    generate,
    improve,
    expand,
    generateStream,
    cancelStream,

    // Direct mutation access for more control
    suggestMutation,
    generateMutation,
    improveMutation,
    expandMutation,
  };
}

// Debounced suggestion hook for inline autocomplete
export function useDebouncedSuggestion(
  delay: number = 500,
  options: UseAIOptions = {}
) {
  const { suggest, isLoading, aiStatus } = useAI(options);
  const [suggestion, setSuggestion] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const requestSuggestion = useCallback((
    currentText: string,
    context?: string,
    fieldName?: string
  ) => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Don't suggest for very short text
    if (currentText.length < 10) {
      setSuggestion('');
      return;
    }

    // Don't suggest if AI is disabled
    if (!aiStatus?.ai_enabled || !aiStatus?.features?.suggestions) {
      return;
    }

    // Debounce the suggestion request
    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await suggest(currentText, context, fieldName);
        setSuggestion(response.content);
      } catch {
        setSuggestion('');
      }
    }, delay);
  }, [suggest, delay, aiStatus]);

  const clearSuggestion = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setSuggestion('');
  }, []);

  return {
    suggestion,
    requestSuggestion,
    clearSuggestion,
    isLoading,
  };
}

export default useAI;
